/**
 * Pure utility functions for SuperLatex alignment measurement and application.
 * Extracted from SuperLatexComponent for reuse in cross-block alignment.
 */

export function getLatexLines(containerEl: HTMLElement): HTMLElement[] {
  return Array.from(containerEl.querySelectorAll('.latex-line')) as HTMLElement[];
}

export function getAllTextNodes(element: HTMLElement): Text[] {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  let node;
  while ((node = walker.nextNode())) {
    textNodes.push(node as Text);
  }

  return textNodes;
}

export function findAlignmentElement(katexHtml: HTMLElement, char: string): HTMLElement | null {
  const textNodes = getAllTextNodes(katexHtml);

  for (const node of textNodes) {
    if (node.textContent?.includes(char)) {
      let element = node.parentElement;

      while (element && element !== katexHtml) {
        if (element.offsetWidth > 0 && element.offsetHeight > 0) {
          return element;
        }
        element = element.parentElement;
      }
    }
  }

  return null;
}

export function measureWidthBeforeAlignChar(lineElement: HTMLElement, alignChar: string): number {
  const katexHtml = lineElement.querySelector('.katex-html') as HTMLElement | null;
  if (!katexHtml) {
    return 0;
  }

  const alignElement = findAlignmentElement(katexHtml, alignChar);
  if (!alignElement) {
    return 0;
  }

  const lineRect = lineElement.getBoundingClientRect();
  const alignRect = alignElement.getBoundingClientRect();

  const computedStyle = window.getComputedStyle(lineElement);
  const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;

  return Math.max(alignRect.left - lineRect.left - paddingLeft, 0);
}

/**
 * Measures and applies alignment padding across multiple containers so that
 * the alignment character lines up vertically across all of them.
 *
 * Each container is expected to have `.latex-line` children with `data-node-key` attributes.
 * Padding is applied directly via `element.style.paddingLeft`.
 */
export function applyGroupAlignment(containers: HTMLElement[], alignChar: string): void {
  if (containers.length === 0) {
    return;
  }

  // Collect all lines from all containers with their measurements
  const allLines: Array<{ element: HTMLElement; width: number }> = [];

  for (const container of containers) {
    const lines = getLatexLines(container);
    for (const line of lines) {
      // Reset any existing group alignment before measuring
      line.style.removeProperty('padding-left');
      line.style.removeProperty('min-width');
      const width = measureWidthBeforeAlignChar(line, alignChar);
      allLines.push({ element: line, width });
    }
  }

  if (allLines.length === 0) {
    return;
  }

  // Step 1: Apply padding-left so the align char lines up within each line
  const maxWidth = Math.max(0, ...allLines.map((l) => l.width));

  for (const { element, width } of allLines) {
    const padding = maxWidth - width;
    if (padding > 0) {
      element.style.paddingLeft = `${padding}px`;
    }
  }

  // Step 2: Equalize line widths across all containers.
  // Each container centers its lines independently (align-items: center).
  // Without equal widths, containers with shorter content shift right,
  // breaking cross-block alignment. Setting min-width to the global max
  // ensures all containers are the same width, so centering is consistent.
  let maxLineWidth = 0;
  for (const { element } of allLines) {
    maxLineWidth = Math.max(maxLineWidth, element.scrollWidth);
  }
  for (const { element } of allLines) {
    element.style.minWidth = `${maxLineWidth}px`;
  }
}
